import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Settings2, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import API from '@/config/api';

/**
 * Admin-only card to toggle which DCF compliance categories generate alerts
 * on the Dashboard and Compliance pages. Settings are per-center.
 */
const ComplianceSettings = ({ onChange }) => {
  const { user, getAuthHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState({});
  const [labels, setLabels] = useState({});

  const isAdmin = user?.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/compliance-settings`, getAuthHeaders());
      setCategories(res.data.categories || {});
      setLabels(res.data.labels || {});
    } catch {
      // non-fatal — just show nothing
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (key) => {
    if (!isAdmin) return;
    const next = { ...categories, [key]: !categories[key] };
    setCategories(next);
    setSaving(true);
    try {
      await axios.put(`${API}/compliance-settings`, { categories: next }, getAuthHeaders());
      toast.success(`${labels[key] || key} ${next[key] ? 'enabled' : 'disabled'}`);
      if (onChange) onChange();
    } catch {
      // revert on error
      setCategories(categories);
      toast.error('Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) return null;

  const disabledCount = Object.values(categories).filter(v => v === false).length;

  return (
    <Card className="card-clay border-l-4 border-l-slate-400" data-testid="compliance-settings-card">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setOpen(o => !o)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
              <Settings2 className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Compliance Alert Settings</CardTitle>
              <p className="text-sm text-muted-foreground">
                {disabledCount > 0
                  ? `${disabledCount} categor${disabledCount === 1 ? 'y' : 'ies'} disabled`
                  : 'All DCF categories active — click to customize'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" data-testid="compliance-settings-toggle">
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground mb-3">
                Disable a category to hide its alerts across Dashboard, Compliance, and reminder badges. Settings apply only to your center.
              </p>
              {Object.keys(labels).map((key) => (
                <div
                  key={key}
                  className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1 pr-4">
                    <p className="text-sm font-medium">{labels[key]}</p>
                  </div>
                  <Switch
                    checked={categories[key] !== false}
                    disabled={saving}
                    onCheckedChange={() => toggle(key)}
                    data-testid={`compliance-cat-toggle-${key}`}
                  />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default ComplianceSettings;
